import { Button, Dialog, DialogBody, DialogFooter, Notification, Text, Textarea } from '@bitrise/bitkit';
import { useForm } from 'react-hook-form';

export type ConfirmAbortBuildDialogProps = {
  isOpen: boolean;
  type: 'build' | 'pipeline';
  error?: string;
  onClose: () => void;
  onConfirm: (abortReason: string) => void;
  isLoading?: boolean;
};

const texts = {
  build: {
    title: 'Abort this Build?',
    content: 'Are you sure you want to abort this build?',
    confirmButton: 'Abort Build',
  },
  pipeline: {
    title: 'Abort this Pipeline?',
    content: 'Are you sure you want to abort this pipeline?',
    confirmButton: 'Abort Pipeline',
  },
};

const ConfirmAbortBuildDialog = ({
  isOpen,
  type,
  error,
  onClose,
  onConfirm,
  isLoading,
}: ConfirmAbortBuildDialogProps): JSX.Element => {
  const { handleSubmit, register, reset } = useForm();

  const { ref: abortReasonRef, ...abortReasonProps } = register('abortReason');

  const onFormSubmit = handleSubmit((values) => {
    onConfirm(values.abortReason);
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog title={`${texts[type].title}`} isOpen={isOpen} onClose={handleClose} as="form" onSubmit={onFormSubmit}>
      <DialogBody>
        <Text>{texts[type].content}</Text>
        <Textarea
          id="abortReason"
          isInvalid={!!error}
          helperText="Optional. The reason will be visible on the Build Details page, and the email notifications."
          label="Abort reason"
          {...abortReasonProps}
          ref={abortReasonRef}
          marginTop={24}
          isDisabled={isLoading}
        />
        {error && (
          <Notification marginTop="16" status="error">
            {error}
          </Notification>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={handleClose} isDisabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {texts[type].confirmButton}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default ConfirmAbortBuildDialog;
